import React, {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { getEnableOperatorVerification } from 'lib/selectors/getEnableOperatorVerification';
import _get from 'lodash/get';
import { toISO } from 'lib/utils/date';

import { getCompany } from 'lib/selectors/getCompany';
import { getMachine } from 'lib/selectors/getMachine';
import { TickerContext } from 'components/TickerProvider';
import BaseJobTimeDialog from 'components/BaseJobTimeDialog';
import PinVerificationModal from 'components/PinVerificationModal';
import useTimeOptions from 'lib/hooks/useTimeOptions';
import {
  TIME_OPTION_FLOW,
  buildSkeletonOptions,
  buildFailureOptions,
  mapApiOptionsToUiOptions,
} from 'lib/utils/timeOptions';
import {
  OPERATOR_AUTH_BUSY,
  resolveSignInBusyPhase,
} from 'lib/utils/operatorAuthBusy';

const LaborTicketSignInModal = ({
  operator,
  onClose,
  onSubmit,
  isOpen,
  isSubmitting = false,
}) => {
  const { t } = useTranslation();

  const nowMs = useContext(TickerContext);

  const company = useSelector(getCompany);
  const machine = useSelector(getMachine);
  const enableOperatorVerification = useSelector(getEnableOperatorVerification);

  const machineRef = machine?.machineRef;
  const tzid = _get(company, 'timezone');

  const openedAtRef = useRef(null);
  if (!openedAtRef.current) {
    openedAtRef.current = toISO(nowMs);
  }

  const nowISO = toISO(nowMs);

  const [showPinVerification, setShowPinVerification] = useState(false);
  const pendingSubmitOptsRef = useRef(null);

  const signInBody = useMemo(() => {
    return {
      signIn: {
        openedAt: openedAtRef.current,
      },
    };
  }, []);

  const { error, options } = useTimeOptions({
    enabled: isOpen && !!machineRef,
    machineRef,
    flow: TIME_OPTION_FLOW.SIGN_IN,
    body: signInBody,
  });

  const phase = resolveSignInBusyPhase({ isSubmitting, showPinVerification });

  const isBusy = !!phase;
  const showDialogOverlay = phase === OPERATOR_AUTH_BUSY.VERIFYING_PIN;

  const dialogBusyTitle = showDialogOverlay ? t(`operatorAuth|${phase}`) : null;

  const optionsList = useMemo(() => {
    if (error || options?.length === 0) {
      return buildFailureOptions(TIME_OPTION_FLOW.SIGN_IN, t, nowISO);
    }

    if (!options) {
      return buildSkeletonOptions(TIME_OPTION_FLOW.SIGN_IN, t, nowISO);
    }

    return mapApiOptionsToUiOptions(
      options,
      TIME_OPTION_FLOW.SIGN_IN,
      t,
      nowISO
    );
  }, [error, options, t, nowISO]);

  const handleClose = useCallback(() => {
    if (isBusy) {
      return undefined;
    }
    return onClose();
  }, [isBusy, onClose]);

  // Handle PIN verification success
  const handlePinSuccess = useCallback(async () => {
    const submitOpts = pendingSubmitOptsRef.current;
    pendingSubmitOptsRef.current = null;

    if (submitOpts) {
      await onSubmit(operator, submitOpts);
    }

    setShowPinVerification(false);
  }, [operator, onSubmit]);

  const handlePinCancel = useCallback(() => {
    pendingSubmitOptsRef.current = null;
    setShowPinVerification(false);
  }, []);

  const handleSelect = useCallback(
    (selectedValue) => {
      if (isBusy) {
        return null;
      }

      const submitOpts = {
        pinToPreviousEvent: false,
        startAtOverride: selectedValue,
      };

      if (enableOperatorVerification && operator?.id) {
        pendingSubmitOptsRef.current = submitOpts;
        setShowPinVerification(true);
        return null;
      }

      return onSubmit(operator, submitOpts);
    },
    [isBusy, onSubmit, operator, enableOperatorVerification]
  );

  if (!isOpen) return null;

  return (
    <>
      <BaseJobTimeDialog
        headerText={t('What time would you like to login at?')}
        optionsList={optionsList}
        tzid={tzid}
        onClose={handleClose}
        onSelect={handleSelect}
        isBusy={showDialogOverlay}
        busyTitle={dialogBusyTitle}
      />

      {enableOperatorVerification && operator?.id && (
        <PinVerificationModal
          isOpen={showPinVerification}
          operatorId={operator.id}
          onSuccess={handlePinSuccess}
          onCancel={handlePinCancel}
        />
      )}
    </>
  );
};

LaborTicketSignInModal.propTypes = {
  operator: PropTypes.object,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  isOpen: PropTypes.bool,
  isSubmitting: PropTypes.bool,
};

export { LaborTicketSignInModal };
